// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  BillingFrequency,
  billingFrequencySchema,
} from '@/constants/billing-frequency';
import {
  CHARGE_SCHEDULE,
  ChargeSchedule,
  chargeScheduleSchema,
} from '@/constants/charge-schedule';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import {
  INVOICE_MODE,
  InvoiceMode,
  invoiceModeSchema,
} from '@/constants/invoice-mode';
import {
  PRICING_MODE,
  PricingMode,
  pricingModeSchema,
} from '@/constants/pricing-mode';
import {
  SerializableCurrency,
  clearableCurrencyInputSchema,
} from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';
import { ItemDetail } from '@/items/api/types';
import { UpdateItemPricingParameters } from '@/items/api/update-item-pricing';
import { useDeleteItemPricing } from '@/items/hooks/use-delete-item-pricing';
import { useUpdateItemPricing } from '@/items/hooks/use-update-item-pricing';
import {
  BILLING_PLAN_INPUT_WIDTH,
  CURRENCY_INPUT_SX,
  FREQUENCY_OPTIONS,
  TOGGLE_BUTTON_GROUP_SX,
  TOGGLE_BUTTON_SX,
  isValidFrequencyPair,
} from '@/project/components/billing-plan-constants';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  BoxProps,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const NO_PRICING_MODE = 'NONE';
const NO_CHARGE_SCHEDULE = 'NONE';
const NO_INVOICE_MODE = 'NONE';
const CLEAR_DEFAULTS_CONFIRMATION_TIMEOUT_MS = 3000;
const INVOICE_FREQUENCY_ORDER_MESSAGE =
  'Invoice schedule cannot be more frequent than the charge schedule';

const infoIconSx = {
  fontSize: '1rem',
  color: 'text.secondary',
  cursor: 'help',
} as const;

const formSchema = z
  .object({
    pricingMode: pricingModeSchema.nullable(),
    unitPrice: clearableCurrencyInputSchema,
    fixedPrice: clearableCurrencyInputSchema,
    chargeSchedule: chargeScheduleSchema.nullable(),
    chargeFrequency: billingFrequencySchema.nullable(),
    invoiceMode: invoiceModeSchema.nullable(),
    invoiceFrequency: billingFrequencySchema.nullable(),
  })
  .superRefine((data, ctx) => {
    if (
      data.pricingMode === null &&
      (data.unitPrice !== null || data.fixedPrice !== null)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Amount fields must be blank when pricing mode is not set',
        path: ['pricingMode'],
      });
    }

    if (
      data.pricingMode === PRICING_MODE.PER_UNIT &&
      data.fixedPrice !== null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Fixed price must be blank in per-unit mode',
        path: ['pricingMode'],
      });
    }

    if (data.pricingMode === PRICING_MODE.FIXED && data.unitPrice !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Unit price must be blank in fixed mode',
        path: ['pricingMode'],
      });
    }

    // A price saved before negatives were refused displays without its sign, so it must be re-entered.
    for (const priceField of ['unitPrice', 'fixedPrice'] as const) {
      if (data[priceField]?.amount.isNegative()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Price must not be negative',
          path: [priceField, 'amount'],
        });
      }
    }

    if (
      data.chargeSchedule === CHARGE_SCHEDULE.MILESTONE &&
      (data.chargeFrequency !== null ||
        data.invoiceMode !== null ||
        data.invoiceFrequency !== null)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cadence fields must be blank for milestone pricing',
        path: ['chargeSchedule'],
      });
    }

    if (
      data.chargeFrequency !== null &&
      data.invoiceFrequency !== null &&
      !isValidFrequencyPair(data.chargeFrequency, data.invoiceFrequency)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: INVOICE_FREQUENCY_ORDER_MESSAGE,
        path: ['invoiceFrequency'],
      });
    }
  });

// The form holds currency fields as their input shape ({ amount, isoCode });
// FormOutput is the post-resolver shape where each clearable price is a
// Currency or null.
interface FormData {
  pricingMode: PricingMode | null;
  unitPrice: SerializableCurrency;
  fixedPrice: SerializableCurrency;
  chargeSchedule: ChargeSchedule | null;
  chargeFrequency: BillingFrequency | null;
  invoiceMode: InvoiceMode | null;
  invoiceFrequency: BillingFrequency | null;
}

type FormOutput = z.output<typeof formSchema>;

interface Props extends Pick<BoxProps, 'sx'> {
  item: ItemDetail;
}

function valuesFromItem(
  item: ItemDetail,
  defaultCurrencyCode: IsoCurrencyCode
): FormData {
  // Seed both price inputs with one currency so toggling pricing mode keeps a
  // consistent denomination: the stored price's currency if any, else the org
  // default.
  const seedCurrencyCode =
    item.defaultPricing?.unitPrice?.isoCode ??
    item.defaultPricing?.fixedPrice?.isoCode ??
    defaultCurrencyCode;
  return {
    pricingMode: item.defaultPricing?.pricingMode ?? null,
    unitPrice: toCurrencyInputValue(
      item.defaultPricing?.unitPrice ?? null,
      seedCurrencyCode
    ),
    fixedPrice: toCurrencyInputValue(
      item.defaultPricing?.fixedPrice ?? null,
      seedCurrencyCode
    ),
    chargeSchedule: item.defaultPricing?.chargeSchedule ?? null,
    chargeFrequency: item.defaultPricing?.chargeFrequency ?? null,
    invoiceMode: item.defaultPricing?.invoiceMode ?? null,
    invoiceFrequency: item.defaultPricing?.invoiceFrequency ?? null,
  };
}

function buildParameters(data: FormOutput): UpdateItemPricingParameters {
  const isPerUnit = data.pricingMode === PRICING_MODE.PER_UNIT;
  const isFixed = data.pricingMode === PRICING_MODE.FIXED;
  const isMilestone = data.chargeSchedule === CHARGE_SCHEDULE.MILESTONE;
  const invoiceFrequency =
    data.chargeFrequency === 'ONCE' ? 'ONCE' : data.invoiceFrequency;

  return {
    pricingMode: data.pricingMode,
    unitPrice: isPerUnit ? (data.unitPrice ?? null) : null,
    fixedPrice: isFixed ? (data.fixedPrice ?? null) : null,
    chargeSchedule: data.chargeSchedule,
    chargeFrequency: isMilestone ? null : data.chargeFrequency,
    invoiceMode: isMilestone ? null : data.invoiceMode,
    invoiceFrequency: isMilestone ? null : invoiceFrequency,
  };
}

export function ItemPricingForm(props: Props) {
  const { user: currentUser } = useCurrentUser();
  const defaultCurrencyCode = currentUser.organization.defaultCurrency;
  const defaultValues = useMemo(
    () => valuesFromItem(props.item, defaultCurrencyCode),
    [props.item, defaultCurrencyCode]
  );
  const [
    isClearDefaultsConfirmationPending,
    setIsClearDefaultsConfirmationPending,
  ] = useState(false);
  const clearDefaultsConfirmationTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const itemKeyRef = useRef(props.item.key);

  const resetClearDefaultsConfirmation = useCallback(() => {
    if (clearDefaultsConfirmationTimeoutRef.current !== null) {
      clearTimeout(clearDefaultsConfirmationTimeoutRef.current);
      clearDefaultsConfirmationTimeoutRef.current = null;
    }
    setIsClearDefaultsConfirmationPending(false);
  }, []);

  const { control, formState, handleSubmit, reset, setValue, watch } = useForm<
    FormData,
    unknown,
    FormOutput
  >({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const updateMutation = useUpdateItemPricing({
    itemKey: props.item.key,
    onSuccess: (data) => {
      resetClearDefaultsConfirmation();
      reset(valuesFromItem(data.item, defaultCurrencyCode));
    },
  });

  const deleteMutation = useDeleteItemPricing({
    itemKey: props.item.key,
    onSuccess: (data) => {
      resetClearDefaultsConfirmation();
      reset(valuesFromItem(data.item, defaultCurrencyCode));
    },
  });

  const chargeSchedule = watch('chargeSchedule');
  const chargeFrequency = watch('chargeFrequency');
  const invoiceFrequency = watch('invoiceFrequency');
  const pricingMode = watch('pricingMode');
  const isPeriodic = chargeSchedule === CHARGE_SCHEDULE.PERIODIC;
  const effectiveInvoiceFrequency =
    chargeFrequency === 'ONCE' ? 'ONCE' : invoiceFrequency;
  const isInvoiceFrequencyPairValid =
    chargeSchedule !== CHARGE_SCHEDULE.PERIODIC ||
    chargeFrequency === null ||
    effectiveInvoiceFrequency === null ||
    isValidFrequencyPair(chargeFrequency, effectiveInvoiceFrequency);
  const mutationError = updateMutation.error ?? deleteMutation.error;
  const pricingValidationMessage =
    formState.errors.pricingMode?.message ?? null;
  const chargeScheduleValidationMessage =
    formState.errors.chargeSchedule?.message ??
    formState.errors.chargeFrequency?.message ??
    null;
  const invoiceScheduleValidationMessage = !isInvoiceFrequencyPairValid
    ? INVOICE_FREQUENCY_ORDER_MESSAGE
    : (formState.errors.invoiceMode?.message ?? null);

  useEffect(() => {
    if (itemKeyRef.current === props.item.key) {
      return;
    }
    itemKeyRef.current = props.item.key;
    resetClearDefaultsConfirmation();
    reset(valuesFromItem(props.item, defaultCurrencyCode));
  }, [props.item, defaultCurrencyCode, reset, resetClearDefaultsConfirmation]);

  useEffect(
    () => () => {
      if (clearDefaultsConfirmationTimeoutRef.current !== null) {
        clearTimeout(clearDefaultsConfirmationTimeoutRef.current);
        clearDefaultsConfirmationTimeoutRef.current = null;
      }
    },
    []
  );

  const handleValidSubmission: SubmitHandler<FormOutput> = (data) => {
    updateMutation.mutate(buildParameters(data));
  };

  const handleClickReset = () => {
    resetClearDefaultsConfirmation();
    reset(defaultValues);
  };

  const handleClickClear = () => {
    if (isClearDefaultsConfirmationPending) {
      resetClearDefaultsConfirmation();
      deleteMutation.mutate();
      return;
    }

    setIsClearDefaultsConfirmationPending(true);
    clearDefaultsConfirmationTimeoutRef.current = setTimeout(() => {
      clearDefaultsConfirmationTimeoutRef.current = null;
      setIsClearDefaultsConfirmationPending(false);
    }, CLEAR_DEFAULTS_CONFIRMATION_TIMEOUT_MS);
  };

  const isMutating = updateMutation.isPending || deleteMutation.isPending;
  const shouldShowActions =
    props.item.defaultPricing !== null || formState.isDirty;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(handleValidSubmission)}
      sx={props.sx}
    >
      <SupportSnackbar
        isOpen={Boolean(mutationError)}
        onClose={() => {
          updateMutation.reset();
          deleteMutation.reset();
        }}
        message={
          mutationError
            ? handleAPIError(mutationError).message
            : 'An error occurred while updating pricing details.'
        }
      />
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Pricing
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Controller
                control={control}
                name="pricingMode"
                render={({ field }) => (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    sx={TOGGLE_BUTTON_GROUP_SX}
                    value={field.value ?? NO_PRICING_MODE}
                    onChange={(
                      _,
                      value: PricingMode | typeof NO_PRICING_MODE | null
                    ) => {
                      if (value === null) return;
                      const nextValue =
                        value === NO_PRICING_MODE ? null : value;
                      field.onChange(nextValue);
                      if (nextValue === PRICING_MODE.PER_UNIT) {
                        setValue('fixedPrice.amount', '', {
                          shouldDirty: true,
                        });
                      } else if (nextValue === PRICING_MODE.FIXED) {
                        setValue('unitPrice.amount', '', { shouldDirty: true });
                      } else {
                        setValue('unitPrice.amount', '', { shouldDirty: true });
                        setValue('fixedPrice.amount', '', {
                          shouldDirty: true,
                        });
                      }
                    }}
                  >
                    <ToggleButton value={NO_PRICING_MODE} sx={TOGGLE_BUTTON_SX}>
                      Not set
                    </ToggleButton>
                    <ToggleButton
                      value={PRICING_MODE.PER_UNIT}
                      sx={TOGGLE_BUTTON_SX}
                    >
                      Per unit
                    </ToggleButton>
                    <ToggleButton
                      value={PRICING_MODE.FIXED}
                      sx={TOGGLE_BUTTON_SX}
                    >
                      Fixed
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
              <Tooltip
                arrow
                title={
                  <Box>
                    <Typography variant="caption" fontWeight={600}>
                      Per unit:
                    </Typography>
                    <Typography variant="caption">
                      {' '}
                      price is multiplied by quantity
                    </Typography>
                    <Box sx={{ mt: 1 }} />
                    <Typography variant="caption" fontWeight={600}>
                      Fixed:
                    </Typography>
                    <Typography variant="caption">
                      {' '}
                      a flat price regardless of quantity
                    </Typography>
                  </Box>
                }
              >
                <InfoOutlined sx={infoIconSx} />
              </Tooltip>
            </Box>
            {pricingMode !== null && (
              <Box sx={{ width: BILLING_PLAN_INPUT_WIDTH, flexShrink: 0 }}>
                {pricingMode === PRICING_MODE.PER_UNIT && (
                  <FormCurrencyTextField
                    fullWidth
                    label="Unit price"
                    name="unitPrice"
                    control={control}
                  />
                )}
                {pricingMode === PRICING_MODE.FIXED && (
                  <FormCurrencyTextField
                    fullWidth
                    label="Fixed price"
                    name="fixedPrice"
                    control={control}
                  />
                )}
              </Box>
            )}
          </Box>
          {pricingValidationMessage !== null && (
            <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
              <Typography variant="body2">
                {pricingValidationMessage}
              </Typography>
            </Alert>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Charge schedule
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Controller
                control={control}
                name="chargeSchedule"
                render={({ field }) => (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    sx={TOGGLE_BUTTON_GROUP_SX}
                    value={field.value ?? NO_CHARGE_SCHEDULE}
                    onChange={(
                      _,
                      value: ChargeSchedule | typeof NO_CHARGE_SCHEDULE | null
                    ) => {
                      if (value === null) return;
                      const nextValue =
                        value === NO_CHARGE_SCHEDULE ? null : value;
                      field.onChange(nextValue);
                      if (
                        nextValue === null ||
                        nextValue === CHARGE_SCHEDULE.MILESTONE
                      ) {
                        setValue('chargeFrequency', null, {
                          shouldDirty: true,
                        });
                        setValue('invoiceMode', null, { shouldDirty: true });
                        setValue('invoiceFrequency', null, {
                          shouldDirty: true,
                        });
                      }
                    }}
                  >
                    <ToggleButton
                      value={NO_CHARGE_SCHEDULE}
                      sx={TOGGLE_BUTTON_SX}
                    >
                      Not set
                    </ToggleButton>
                    <ToggleButton
                      value={CHARGE_SCHEDULE.PERIODIC}
                      sx={TOGGLE_BUTTON_SX}
                    >
                      Periodic
                    </ToggleButton>
                    <ToggleButton
                      value={CHARGE_SCHEDULE.MILESTONE}
                      sx={TOGGLE_BUTTON_SX}
                    >
                      Milestone
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
              <Tooltip
                arrow
                title={
                  <Box>
                    <Typography variant="caption" fontWeight={600}>
                      Periodic:
                    </Typography>
                    <Typography variant="caption">
                      {' '}
                      charges recur at a regular frequency
                    </Typography>
                    <Box sx={{ mt: 1 }} />
                    <Typography variant="caption" fontWeight={600}>
                      Milestone:
                    </Typography>
                    <Typography variant="caption">
                      {' '}
                      charges occur at project milestones
                    </Typography>
                  </Box>
                }
              >
                <InfoOutlined sx={infoIconSx} />
              </Tooltip>
            </Box>
            {isPeriodic && (
              <Controller
                control={control}
                name="chargeFrequency"
                render={({ field }) => (
                  <FormControl size="small" sx={CURRENCY_INPUT_SX}>
                    <Select
                      displayEmpty
                      inputProps={{ 'aria-label': 'Charge frequency' }}
                      value={field.value ?? ''}
                      onChange={(event) => {
                        const value = (event.target.value ||
                          null) as BillingFrequency | null;
                        field.onChange(value);
                        if (value === 'ONCE') {
                          setValue('invoiceFrequency', 'ONCE', {
                            shouldDirty: true,
                          });
                        }
                      }}
                    >
                      <MenuItem value="">Not set</MenuItem>
                      {FREQUENCY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            )}
          </Box>
          {chargeScheduleValidationMessage !== null && (
            <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
              <Typography variant="body2">
                {chargeScheduleValidationMessage}
              </Typography>
            </Alert>
          )}
        </Paper>

        {chargeSchedule !== null && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Invoice schedule
            </Typography>
            {chargeSchedule === CHARGE_SCHEDULE.MILESTONE ? (
              <Typography variant="body2" color="text.secondary">
                Invoice schedule is determined by milestone achievement
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Controller
                    control={control}
                    name="invoiceMode"
                    render={({ field }) => (
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        sx={TOGGLE_BUTTON_GROUP_SX}
                        value={field.value ?? NO_INVOICE_MODE}
                        onChange={(
                          _,
                          value: InvoiceMode | typeof NO_INVOICE_MODE | null
                        ) => {
                          if (value === null) return;
                          field.onChange(
                            value === NO_INVOICE_MODE ? null : value
                          );
                        }}
                      >
                        <ToggleButton
                          value={NO_INVOICE_MODE}
                          sx={TOGGLE_BUTTON_SX}
                        >
                          Not set
                        </ToggleButton>
                        <ToggleButton
                          value={INVOICE_MODE.ADVANCE}
                          sx={TOGGLE_BUTTON_SX}
                        >
                          In advance
                        </ToggleButton>
                        <ToggleButton
                          value={INVOICE_MODE.ARREARS}
                          sx={TOGGLE_BUTTON_SX}
                        >
                          In arrears
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                  <Tooltip
                    arrow
                    title={
                      <Box>
                        <Typography variant="caption" fontWeight={600}>
                          In advance:
                        </Typography>
                        <Typography variant="caption">
                          {' '}
                          bill at the start of each billing period
                        </Typography>
                        <Box sx={{ mt: 1 }} />
                        <Typography variant="caption" fontWeight={600}>
                          In arrears:
                        </Typography>
                        <Typography variant="caption">
                          {' '}
                          bill at the end of each billing period
                        </Typography>
                      </Box>
                    }
                  >
                    <InfoOutlined sx={infoIconSx} />
                  </Tooltip>
                </Box>
                <Controller
                  control={control}
                  name="invoiceFrequency"
                  render={({ field }) => (
                    <FormControl size="small" sx={CURRENCY_INPUT_SX}>
                      <Select
                        displayEmpty
                        inputProps={{ 'aria-label': 'Invoice frequency' }}
                        value={
                          chargeFrequency === 'ONCE'
                            ? 'ONCE'
                            : (field.value ?? '')
                        }
                        onChange={(event) =>
                          field.onChange(
                            (event.target.value ||
                              null) as BillingFrequency | null
                          )
                        }
                        disabled={chargeFrequency === 'ONCE'}
                      >
                        <MenuItem value="">Not set</MenuItem>
                        {FREQUENCY_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>
            )}
            {invoiceScheduleValidationMessage !== null && (
              <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
                <Typography variant="body2">
                  {invoiceScheduleValidationMessage}
                </Typography>
              </Alert>
            )}
          </Paper>
        )}

        {shouldShowActions && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            {props.item.defaultPricing !== null && (
              <Button
                color="error"
                variant={
                  isClearDefaultsConfirmationPending ? 'contained' : 'text'
                }
                onClick={handleClickClear}
                disabled={isMutating}
              >
                {isClearDefaultsConfirmationPending
                  ? 'Click again to confirm'
                  : 'Clear defaults'}
              </Button>
            )}
            {formState.isDirty && (
              <>
                <Button onClick={handleClickReset} disabled={isMutating}>
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={isMutating}
                >
                  Save
                </Button>
              </>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
